import { action, computed, makeObservable, observable } from 'mobx';
import { Inject, Service } from 'typedi';
import { IFilters, IMeeting, ISearchAreaOption } from '../types';
import { PrivateCalendarsStore } from '../../store';
import { getDateInUserTimezone } from '..';

@Service()
export class FilterService {

  @Inject(() => PrivateCalendarsStore)
  private privateCalendarStore: PrivateCalendarsStore;
  
  sessionFilters: IFilters = JSON.parse(sessionStorage.getItem('filters'));

  @observable filters: IFilters = {
    search: this.sessionFilters?.search ?? '',
    searchArea: this.sessionFilters?.searchArea ?? [this.searchAreaOptions[0]],
    who: this.sessionFilters?.who ?? [],
    description: this.sessionFilters?.description ?? '',
    where: this.sessionFilters?.where !== null ? Number(this.sessionFilters?.where) : null,
    format: this.sessionFilters?.format ?? null,
    startInterval: this.sessionFilters?.startInterval
      ? new Date(this.sessionFilters.startInterval)
      : null,
    endInterval: this.sessionFilters?.endInterval
      ? new Date(this.sessionFilters.endInterval)
      : null,
    isShowPrevMeetings: this.sessionFilters?.isShowPrevMeetings ?? true,
  };

  @observable selectEmployeeId: number | null = null;

  @observable selectRoomCabinetNumber: number | null = null;

  @observable showAllRoomsMeetings: boolean = JSON.parse(sessionStorage.getItem('isAllRoom')) as boolean ?? false;

  @computed get selectedItem() {
    return { 
      allRoom: this.showAllRoomsMeetings,
      room: this.selectRoomCabinetNumber, 
      employee: this.selectEmployeeId,
    }
  }
  
  @observable.shallow notCheckedCalendarsIds: number[] = (JSON.parse(localStorage.getItem('notCheckedCalendars')) as number[]) ?? [];
  
  constructor() {
    makeObservable(this);
  }

  isValidMeeting = (meeting: IMeeting, offset: number) => {
    const date = new Date();
    if (this.filters.searchArea[0]?.mode === 'ACTIVE' && this.notCheckedCalendarsIds.includes(meeting.calendarId)) return false;
    if (typeof this.filters.searchArea[0]?.mode === 'number' && !this.filters.searchArea.some(calendar => typeof calendar.data !== 'string' && calendar.data.id === meeting.calendarId)) return false;
    if (!!this.filters.search.length && !meeting.meetingName.toLowerCase().includes(this.filters.search.toLowerCase())) return false;

    if (this.filters.where && this.filters.where !== meeting.room?.cabinetNumber) return false;
    if (this.filters.format && this.filters.format !== meeting.format) return false;
    if (this.filters.description && !meeting.description?.includes(this.filters.description)) return false;
    if (!!this.filters.who.length) {
      // Если юзеров несколько, то отображаются только те встречи, где они все состоят как участники
      if (this.filters.who.length > 1 && !this.filters.who.every(memberFilterId => meeting.members?.some(member => member?.id === memberFilterId))) return false;
      // Иначе выбрали одного юзера. Тогда показываются встречи, где он инициатор и его внешние встречи(они отобразятся для текущего юзера или для сотрудника, выбранного из левой панели)
      if (!(meeting.members?.some(member => member?.id === this.filters.who[0]) || meeting.initiator.id === this.filters.who[0])) return false;
    }

    if (!this.filters.isShowPrevMeetings && meeting.endDate < getDateInUserTimezone(date.toString(), offset)) return false;
    return true;
  };

  @computed get searchAreaOptions(): ISearchAreaOption[] {
    return [
      {
        mode: 'ACTIVE',
        data: 'Активные календари'
      },
      {
        mode: 'ALL',
        data: 'Все календари',
      },
      ...(!!this.privateCalendarStore?.data.length
        ? [
          { mode: this.privateCalendarStore.innerCalendar.id, data: this.privateCalendarStore.innerCalendar },
          ...this.privateCalendarStore.data.map(calendar => ({ mode: calendar.id, data: calendar }))
        ]
        : []
      )
    ];
  }
  
  @action setSelectRoomResetEmployee = (cabinetNumber: number) => {
    this.selectRoomCabinetNumber = (this.selectRoomCabinetNumber === cabinetNumber ? null : cabinetNumber);
    this.selectEmployeeId = null;
    this.setAllRoom(false);
  };

  @action setShowAllRoomsMeetings = (value: boolean) => {
    this.setAllRoom(value);
    this.selectEmployeeId = null;
    this.selectRoomCabinetNumber = null;
  };

  @action setAllRoom = (value: boolean) => {
    this.showAllRoomsMeetings = value;
    sessionStorage.setItem('isAllRoom', `${value}`);
  }

  @action resetFilters = () => {
    this.filters = {
      search: '',
      searchArea: [this.searchAreaOptions[0]],
      who: [],
      description: '',
      where: null,
      format: null,
      startInterval: null,
      endInterval: null,
      isShowPrevMeetings: true,
    };
    sessionStorage.removeItem('filters');
  }

  @action setSelectEmployeeResetRoom = (id?: number) => {
    this.selectEmployeeId = (this.selectEmployeeId === id ? null : id);
    this.selectRoomCabinetNumber = null;
    this.setAllRoom(false);
  };
}
